import { useState, useRef, useEffect, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Send } from "lucide-react";
import { inboxMessagesApi } from "@/api/inbox-messages";

interface MessageInputProps {
  inboxId: number;
  onSent: () => void;
}

export function MessageInput({ inboxId, onSent }: MessageInputProps) {
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const maxHeight = 5 * 24;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  };

  useEffect(() => {
    adjustHeight();
  }, [content]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const text = content.trim();
    if (!text || sending) return;

    setSending(true);
    try {
      await inboxMessagesApi.create({
        id_inbox: inboxId,
        direction: "outbound",
        content: text,
        content_type: "text",
        source: "agent",
        status: "sent",
      });
      setContent("");
      onSent();
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="border-t">
      <form onSubmit={handleSubmit} className="flex items-end gap-2 p-3">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Digite uma mensagem..."
          rows={1}
          className="min-h-[40px] max-h-[120px] resize-none"
        />
        <Button
          type="submit"
          size="icon"
          disabled={!content.trim() || sending}
          className="shrink-0"
        >
          {sending ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </form>
      <p className="px-3 pb-2 text-[10px] text-muted-foreground">
        Enter para enviar &middot; Shift+Enter nova linha
      </p>
    </div>
  );
}
